/**
 * Recordings, held once read.
 *
 * A call's audio is fetched as bytes (`fetchCallRecording`) and played
 * from an object URL, and the player used to fetch it on every mount
 * and revoke it on every unmount — so on the job page (#115) each open
 * of a call moved the file again, and a person going between two calls
 * on one thread waited for each of them each time. What is held here
 * is what has been read: the bytes once, the URL once, the waveform
 * once, for as long as the app is open. A file is read when an
 * enquiry is opened — the page waits for its recordings before it is
 * drawn (`readRecordings`) — and never again.
 *
 * Read means ready to be drawn: the bytes, and the waveform and the
 * length decoded from them, so the player on the page's first paint
 * has its bars and its clock already. It decoded only when a player
 * asked, and the page drew with the row flat and the clock at
 * 0:00 / 0:00 for the half-second the decode took. A read that failed
 * is not held: the next open asks again and says what went wrong. A
 * recording that cannot be decoded is held as one with no bars, which
 * is drawn flat and still plays.
 */
import { fetchCallRecording } from "./arrivals-api";
import { type Decoded, peaksOf } from "./waveform";

type Held = {
  blob: Promise<Blob>;
  src: Promise<string>;
  /** The object URL once there is one, for a player drawn after it. */
  url: string | null;
  waveform?: Promise<Decoded>;
  /** The bars and length once decoded, for a player drawn after it. */
  decoded: Decoded | null;
};

/** What a recording that cannot be decoded is held as: flat, no length. */
const UNDECODED: Decoded = { bars: [], seconds: 0 };

const held = new Map<string, Held>();

function hold(id: string): Held {
  const have = held.get(id);
  if (have) return have;
  const blob = fetchCallRecording(id);
  const entry: Held = {
    blob,
    src: blob.then((bytes) => URL.createObjectURL(bytes)),
    url: null,
    decoded: null,
  };
  entry.src.then(
    (url) => {
      entry.url = url;
    },
    () => {
      if (held.get(id) === entry) held.delete(id);
    },
  );
  held.set(id, entry);
  return entry;
}

/** One call's audio as a URL a player can be given, read once. */
export function recordingSrc(id: string): Promise<string> {
  return hold(id).src;
}

/** The URL at once where the call's audio is already in hand, else null. */
export function heldRecordingSrc(id: string): string | null {
  return held.get(id)?.url ?? null;
}

/**
 * One call's waveform and length, decoded once from the bytes in hand.
 * No bars and no length where the browser could not decode it; the
 * recording still plays.
 */
export function recordingWaveform(id: string): Promise<Decoded> {
  const entry = hold(id);
  if (!entry.waveform) {
    entry.waveform = entry.blob.then(async (bytes) => {
      const decoded = (await peaksOf(bytes)) ?? UNDECODED;
      entry.decoded = decoded;
      return decoded;
    });
  }
  return entry.waveform;
}

/** The bars at once where they are already decoded, else null. */
export function heldRecordingBars(id: string): number[] | null {
  return held.get(id)?.decoded?.bars ?? null;
}

/** The length in seconds at once where it is already decoded, else 0. */
export function heldRecordingSeconds(id: string): number {
  return held.get(id)?.decoded?.seconds ?? 0;
}

/**
 * How long a page waits for its recordings before it draws anyway. A
 * file still moving after this finishes in its player, as it did
 * before the page waited at all.
 */
export const RECORDINGS_WAIT_MS = 15_000;

/**
 * Reads the given calls' audio and decodes each one's waveform and
 * length, all at once, and settles when every one is in hand or has
 * failed — or when `capMs` has passed, whichever is first. It never rejects: a file that cannot be read is left for the
 * player to report, and a page that waits on this is never held by one.
 */
export function readRecordings(
  ids: string[],
  capMs: number = RECORDINGS_WAIT_MS,
): Promise<void> {
  if (ids.length === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, capMs);
    const read = (id: string) =>
      Promise.allSettled([recordingSrc(id), recordingWaveform(id)]);
    void Promise.allSettled(ids.map(read)).then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/** Lets go of everything held. For tests, which draw the same calls over. */
export function forgetRecordings(): void {
  for (const entry of held.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
  }
  held.clear();
}
